// frontend/src/components/sps/ImportProgress.jsx
// Real progress display driven by useBatchImport state.
// Every number shown is exact — from the actual server responses.

import { ProgressBar, Row, Col, Alert } from 'react-bootstrap';

function Stat({ label, value, colour = 'secondary' }) {
  return (
    <div className="text-center">
      <div className={`fw-bold text-${colour}`}
        style={{ fontSize: '1.05rem', fontFamily: 'monospace' }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="text-muted" style={{ fontSize: '.7rem', marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function ImportProgress({ state }) {
  const {
    status, totalRows, processed, pct,
    inserted, updated, skipped, errors,
    errorDetails, currentBatch, totalBatches,
  } = state;

  if (status === 'idle') return null;

  const isReading   = status === 'reading';
  const isImporting = status === 'importing';
  const isDone      = status === 'done';
  const isError     = status === 'error';
  const isRunning   = isReading || isImporting;

  const barVariant = isError ? 'danger' : isDone ? 'success' : 'primary';

  return (
    <div className="mt-3">

      {/* Label row */}
      <div className="d-flex justify-content-between align-items-baseline mb-1">
        <span className="small fw-semibold" style={{
          color: isError ? '#dc3545' : isDone ? '#1a6e3c' : '#1e3a5f'
        }}>
          {isReading   && '📖 Reading file…'}
          {isImporting && '⬆ Uploading to database…Time to go for coffee'}
          {isDone      && '✓ Import complete'}
          {isError     && '✗ Import failed'}
        </span>

        {/* Exact batch counter */}
        {isImporting && totalBatches > 0 && (
          <span style={{ fontFamily: 'monospace', fontSize: '.82rem', color: '#555' }}>
            batch {currentBatch.toLocaleString()} / {totalBatches.toLocaleString()}
          </span>
        )}
      </div>

      {/* Progress bar — exact percentage */}
      <ProgressBar
        animated={isRunning}
        now={isReading ? 100 : pct}
        variant={isReading ? 'info' : barVariant}
        striped={isReading}
        style={{ height: 28, borderRadius: 4 }}
        label={!isReading && pct > 6 ? `${pct}%` : ''}
      />

      {/* Exact row counter */}
      {(isImporting || isDone) && totalRows > 0 && (
        <div className="mt-2 d-flex justify-content-between align-items-center">
          <span style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: 700 }}>
            <span style={{ color: isDone ? '#1a6e3c' : '#0d6efd' }}>
              {processed.toLocaleString()}
            </span>
            <span style={{ color: '#888', fontWeight: 400 }}>
              {' '}/ {totalRows.toLocaleString()} rows
            </span>
          </span>
          {isDone && (
            <span style={{ fontFamily: 'monospace', fontSize: '.8rem', color: '#888' }}>
              100% complete
            </span>
          )}
        </div>
      )}

      {/* Live stats while running and on completion */}
      {(isImporting || isDone) && (
        <Row className="g-2 mt-2">
          <Col><Stat label="Inserted" value={inserted} colour="success"   /></Col>
          <Col><Stat label="Updated"  value={updated}  colour="primary"   /></Col>
          <Col><Stat label="Skipped"  value={skipped}  colour="secondary" /></Col>
          <Col><Stat label="Errors"   value={errors}   colour={errors > 0 ? 'danger' : 'secondary'} /></Col>
        </Row>
      )}

      {/* Error message */}
      {isError && errorDetails?.length > 0 && (
        <Alert variant="danger" className="mt-2 mb-0 py-2" style={{ fontSize: '.88rem' }}>
          {errorDetails[0]}
        </Alert>
      )}

      {/* Row-level error details */}
      {(isDone || isError) && errorDetails?.length > 0 && (
        <details className="mt-2">
          <summary className="text-warning small" style={{ cursor: 'pointer' }}>
            {errorDetails.length} row error{errorDetails.length !== 1 ? 's' : ''} — click to view
          </summary>
          <pre style={{ fontSize: '.7rem', maxHeight: 120, overflowY: 'auto',
            background: '#fff8e1', padding: '6px 8px', borderRadius: 4, marginTop: 4 }}>
            {errorDetails.join('\n')}
          </pre>
        </details>
      )}

    </div>
  );
}
